import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../../../lib/cn';
import { SidebarActiveIndicator } from './SidebarActiveIndicator';

/** サイドバーのナビ項目。 */
export interface SidebarNavItem {
  /** 一意キー */
  key: string;
  /** 表示ラベル（呼び出し側で翻訳済みの文字列を渡す） */
  label: string;
  /** リンク先パス */
  path: string;
  /** アイコン要素 */
  icon: ReactNode;
  /**
   * 直前の項目の子としてインデント表示するか。true なら左余白を増やし
   * フォントを一段小さくして親子関係を視覚化する。
   */
  nested?: boolean;
  /**
   * この項目を子としてぶら下げる親項目の key。
   * 設定すると親の折り畳み制御に追従し、親が collapsed の時はこの項目は非表示になる。
   * 親項目自身はラベル左にシェブロンが描画され、クリックでナビゲートしつつ折り畳みもトグルする。
   */
  parentKey?: string;
  /**
   * アクティブ判定から除外するか。`true` の項目はパスが現在地に一致してもアクティブ表示しない。
   * ナビゲーション先ではなくダイアログ等を開くアクション項目（別項目とパスが重複し得る）に使う。
   */
  noActive?: boolean;
  /**
   * ナビゲーション先を持たない「フォルダ」項目か。`true` の項目はクリックしてもページ遷移せず、
   * 子項目の展開/折り畳みトグルのみを行う（`path` はアクティブ判定にのみ使用）。
   * アプリ側の `renderNavLink` はこのフラグを見てリンクではなくボタン要素を描画する。
   */
  folder?: boolean;
}

/** サイドバーのナビゲーショングループ。 */
export interface SidebarNavGroup {
  /** グループ見出し（省略時は見出しなし） */
  label?: string;
  /** グループ内のナビ項目 */
  items: SidebarNavItem[];
  /** true なら直前に区切り線を描画する */
  dividerBefore?: boolean;
}

/** renderNavLink に渡される引数。リンク要素のみアプリ側で描画する。 */
export interface SidebarRenderNavLinkArgs {
  /** 対象ナビ項目 */
  item: SidebarNavItem;
  /** 現在パスがこの項目配下かどうか */
  isActive: boolean;
  /** リンク要素に適用する className（デザインシステム側が決定） */
  className: string;
  /** リンク要素の子（アイコン + ラベル） */
  children: ReactNode;
  /** リンククリック時に呼ぶハンドラ（モバイルドロワーを閉じる用） */
  onNavigate?: () => void;
  /**
   * 子項目を持つ親項目（折り畳みトグル兼用）に渡される展開状態。
   * `true` なら展開中、`false` なら折り畳み中。子を持たない通常項目は `undefined`。
   * アプリ側は `<a>`/`<Link>` に `aria-expanded` として透過するだけでよい。
   */
  ariaExpanded?: boolean;
}

export interface SidebarProps {
  /** アプリ名（モバイルドロワーのヘッダーに表示） */
  appName: string;
  /** ロゴスロット（モバイルドロワーのヘッダーに表示） */
  logo?: ReactNode;
  /** ナビゲーショングループ一覧 */
  navGroups: SidebarNavGroup[];
  /** リンク要素の描画関数。route tree 型に依存しないようアプリ側が注入する */
  renderNavLink: (args: SidebarRenderNavLinkArgs) => ReactNode;
  /** 現在のパス（アクティブ判定に使用） */
  currentPath: string;
  /** フッタースロット（組織名表記など） */
  footer?: ReactNode;
  /** モバイルでの表示状態 */
  isOpen?: boolean;
  /** モバイルドロワーを閉じる処理 */
  onClose?: () => void;
  /** デスクトップでの折りたたみ（アイコンレール化）状態 */
  isCollapsed?: boolean;
  /** デスクトップ折りたたみの切替 */
  onToggleCollapse?: () => void;
}

/** アイコンレール時のサイドバー幅（px）。 */
const RAIL_WIDTH = 64;
/** 展開時サイドバー幅の下限・上限・既定（px）。 */
const MIN_WIDTH = 200;
const MAX_WIDTH = 480;
const DEFAULT_WIDTH = 256;
/** ドラッグ調整した展開幅を保存する localStorage キー（オリジン単位で衝突しない）。 */
const WIDTH_STORAGE_KEY = 'cortex-sidebar-width';
/** 折り畳み中の親項目 key 集合を保存する localStorage キー。値は JSON 配列。 */
const COLLAPSED_PARENTS_STORAGE_KEY = 'cortex-sidebar-collapsed-parents';

/** localStorage から折り畳み中の親 key 集合を読み出す（破損時は空集合）。 */
function readStoredCollapsedParents(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSED_PARENTS_STORAGE_KEY);
    if (raw === null) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((v): v is string => typeof v === 'string'));
  } catch {
    return new Set();
  }
}

/** 展開幅を下限・上限内に収める。 */
function clampWidth(width: number): number {
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, width));
}

/** localStorage から保存済みの展開幅を読み出す（無効値なら既定幅）。 */
function readStoredWidth(): number {
  const stored = Number(localStorage.getItem(WIDTH_STORAGE_KEY));
  return Number.isFinite(stored) && stored > 0 ? clampWidth(stored) : DEFAULT_WIDTH;
}

/**
 * ナビリンクに適用する className を、アクティブ状態と nested 指定に応じて返す。
 * nested 指定時は左インデント + フォント縮小で親項目の子として視覚化する。
 * アクティブ項目の背景は `SidebarActiveIndicator`（現在地へスライドする 1 枚）が描くため、
 * ここでは文字色だけを変える。左右余白は全項目 `mx-2` で揃え、選択背景の `left-2 right-2` と一致させる。
 */
function navLinkClassName(isActive: boolean, nested: boolean): string {
  return cn(
    'flex items-center gap-3 rounded-lg font-medium transition-colors',
    nested ? 'pl-10 pr-4 py-2 mx-2 text-xs' : 'px-4 py-2.5 mx-2 text-sm',
    isActive
      ? 'text-[var(--color-accent-primary)]'
      : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]',
  );
}

/**
 * @graph-stack web-design-system
 * @graph-domain Engineering
 * @graph-business サイドバーナビゲーション。navGroups を props で受け取り、リンク描画を renderNavLink で注入することで
 *  ルーティング実装に依存しない。モバイルはスライドインドロワー、デスクトップは折りたたみ可能。
 *  折りたたみ時はアイコンのみのレール（64px）になり、レールへマウスを乗せるとメインコンテンツの上へ
 *  オーバーレイ展開する。展開時は右端のハンドルをドラッグして幅を調整でき、localStorage に永続化する。
 *  parentKey を持つ項目は親項目の折り畳みに追従し、親項目クリックでナビゲートしつつ展開状態を
 *  トグルする。展開状態も localStorage に永続化する
 * @graph-connects none
 */
export function Sidebar({
  appName,
  logo,
  navGroups,
  renderNavLink,
  currentPath,
  footer,
  isOpen = false,
  onClose,
  isCollapsed = false,
  onToggleCollapse,
}: SidebarProps): ReactNode {
  // 折りたたみ時、レールへホバー中はオーバーレイ展開する。
  const [hovered, setHovered] = useState(false);
  // 展開表示するか（折りたたみ解除中、または折りたたみレールへホバー中）。
  const showExpanded = !isCollapsed || hovered;

  // 展開幅（ドラッグで調整可能、localStorage 永続化）。
  const [expandedWidth, setExpandedWidth] = useState<number>(readStoredWidth);
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartRef = useRef({ startX: 0, startWidth: DEFAULT_WIDTH });

  // 折り畳み中の親項目 key 集合（localStorage 永続化）。
  const [collapsedParents, setCollapsedParents] = useState<Set<string>>(readStoredCollapsedParents);

  // 折り畳み状態の変更を localStorage に永続化する。
  useEffect(() => {
    localStorage.setItem(COLLAPSED_PARENTS_STORAGE_KEY, JSON.stringify([...collapsedParents]));
  }, [collapsedParents]);

  /** 指定した親項目 key の折り畳み状態を反転する。 */
  const toggleParentCollapsed = (parentKey: string): void => {
    setCollapsedParents((prev) => {
      const next = new Set(prev);
      if (next.has(parentKey)) next.delete(parentKey);
      else next.add(parentKey);
      return next;
    });
  };

  // リサイズ中だけ document に mousemove/mouseup を張る（アンマウント時も確実に解除）。
  useEffect(() => {
    if (!isResizing) return;
    const onMove = (event: MouseEvent): void => {
      const { startX, startWidth } = resizeStartRef.current;
      setExpandedWidth(clampWidth(startWidth + event.clientX - startX));
    };
    const onUp = (): void => {
      setIsResizing(false);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isResizing]);

  // 展開幅の変更を localStorage に永続化する。
  useEffect(() => {
    localStorage.setItem(WIDTH_STORAGE_KEY, String(expandedWidth));
  }, [expandedWidth]);

  const onResizeStart = (event: React.MouseEvent): void => {
    event.preventDefault();
    resizeStartRef.current = { startX: event.clientX, startWidth: expandedWidth };
    setIsResizing(true);
  };

  // 全ナビ項目のパス（noActive 除外）を事前収集。prefix マッチの抑制判定に使う。
  const allNavPaths = navGroups.flatMap((g) =>
    g.items.filter((i) => !i.noActive).map((i) => i.path),
  );

  // パスセグメント境界での前方一致でアクティブ判定する。完全一致、または現在パスが
  // `path + '/'` で始まる（= path の子ルート）場合のみアクティブ。ただし、より具体的な
  // パスを持つ別のナビ項目が currentPath にマッチする場合は prefix マッチを抑制する
  // （例: `/purchase-orders/new` が別項目にある時、`/purchase-orders` をアクティブにしない）。
  // ルート '/' は全パスの接頭辞になるため完全一致のみとする。
  const isActive = (path: string): boolean => {
    if (path === '/') return currentPath === '/';
    if (currentPath === path) return true;
    if (!currentPath.startsWith(path + '/')) return false;
    return !allNavPaths.some(
      (p) =>
        p !== path &&
        p.length > path.length &&
        (currentPath === p || currentPath.startsWith(p + '/')),
    );
  };

  // 各 group ごとに「子を持つ親 key の集合」を事前計算する。
  // 親項目クリック時にシェブロンを描画し、折り畳みトグル対象になるかの判定に使う。
  const parentKeysWithChildren = new Set<string>();
  for (const group of navGroups) {
    for (const item of group.items) {
      if (item.parentKey !== undefined && item.parentKey !== '') {
        parentKeysWithChildren.add(item.parentKey);
      }
    }
  }

  // スライドする選択背景の対象 key。描画ループと同じ判定で、上から最初に一致した項目を採る。
  const activeItemKey = navGroups
    .flatMap((group) => group.items)
    .find((item) => item.noActive !== true && isActive(item.path))?.key;

  /**
   * ナビゲーション本体を描画する。
   * @param expanded - ラベル・グループ見出し・フッターを表示するか（false ならアイコンのみのレール）
   */
  const renderSidebarContent = (expanded: boolean): ReactNode => (
    <nav className="flex flex-col h-full">
      {/* Navigation items。選択背景を項目の下へ重ねるため relative + isolate にする
        （isolate が無いと -z-10 の選択背景が祖先の背景より奥へ抜ける）。
        グループ間の余白は内側の div が持つ: 選択背景を space-y-3 の兄弟にすると
        先頭グループに余計な margin が付く */}
      <div className="relative isolate flex-1 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] overflow-y-auto overflow-x-hidden">
        <SidebarActiveIndicator activeKey={activeItemKey} />
        <div className="space-y-3">
          {navGroups.map((group) => (
            <div key={group.label ?? group.items[0]?.key ?? 'divider'} className="space-y-1">
              {group.dividerBefore === true && (
                <hr className="mx-4 my-1 border-t border-[var(--color-border)]" />
              )}
              {group.label !== undefined && group.label !== '' && (
                <p
                  className={cn(
                    'px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)] whitespace-nowrap transition-opacity duration-200',
                    !expanded && 'opacity-0',
                  )}
                >
                  {group.label}
                </p>
              )}
              {group.items.map((item) => {
                // 親が折り畳まれている子項目は描画しない。
                if (
                  item.parentKey !== undefined &&
                  item.parentKey !== '' &&
                  collapsedParents.has(item.parentKey)
                ) {
                  return null;
                }
                // noActive 項目（アクション項目）はパスが一致してもアクティブ表示しない。
                const active = item.noActive !== true && isActive(item.path);
                const hasChildren = parentKeysWithChildren.has(item.key);
                const isCollapsedParent = hasChildren && collapsedParents.has(item.key);
                const isFolder = item.folder === true;
                // クリック時の挙動:
                // - フォルダ親項目 (folder): 遷移せず折り畳みトグルのみ。子を選べるようモバイルドロワーは
                //   閉じない（onClose を呼ばない）。
                // - 遷移する親項目 (子あり・非フォルダ): 折り畳みトグル + navigate に伴いドロワーを閉じる。
                // - 通常項目: ドロワーを閉じるだけ（navigate は renderNavLink 側の Link が担う）。
                const onClick =
                  hasChildren && isFolder
                    ? (): void => {
                        toggleParentCollapsed(item.key);
                      }
                    : hasChildren
                      ? (): void => {
                          toggleParentCollapsed(item.key);
                          onClose?.();
                        }
                      : onClose;
                return (
                  // data-sidebar-item は選択背景（SidebarActiveIndicator）が計測対象の
                  // リンク要素を引くための目印。直下の 1 要素がリンクである前提に依存する。
                  <div key={item.key} data-sidebar-item={item.key}>
                    {renderNavLink({
                      item,
                      isActive: active,
                      className: navLinkClassName(active, item.nested === true),
                      onNavigate: onClick,
                      // 子を持つ親項目のみ aria-expanded を渡す。スクリーンリーダー利用者へ
                      // 「この link が折り畳みも兼ねている」状態を明示する。
                      ariaExpanded: hasChildren ? !isCollapsedParent : undefined,
                      children: (
                        <>
                          <span className="shrink-0">{item.icon}</span>
                          <span
                            className={cn(
                              'truncate transition-opacity duration-200',
                              // chevron 兄弟を右端へ寄せる必要があるのは親項目だけなので flex-1
                              // も親項目だけに付ける。`min-w-0` は flex 子要素で truncate を
                              // 確実に効かせるために必要（無いと flex の min-content で溢れる）。
                              hasChildren && 'flex-1 min-w-0',
                              !expanded && 'opacity-0',
                            )}
                          >
                            {item.label}
                          </span>
                          {hasChildren && (
                            <svg
                              className={cn(
                                'w-3 h-3 shrink-0 transition-all duration-200',
                                !expanded && 'opacity-0',
                                isCollapsedParent ? '-rotate-90' : 'rotate-0',
                              )}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          )}
                        </>
                      ),
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      {footer !== undefined && footer !== null && (
        <div
          className={cn(
            'p-4 border-t border-[var(--color-border)] transition-opacity duration-200',
            !expanded && 'opacity-0',
          )}
        >
          {footer}
        </div>
      )}
    </nav>
  );

  return (
    <>
      {/* Mobile overlay — z-modal でヘッダー(z-sticky)より前面に出す */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[var(--z-modal)] bg-black/50 md:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Mobile sidebar (slide in) — z-modal でヘッダーより前面、× ボタンを必ずクリック可能にする */}
      <aside
        className={cn(
          'fixed top-0 left-0 z-[var(--z-modal)] h-full w-64 bg-[var(--color-bg-surface)] border-r border-[var(--color-border)] transform transition-transform duration-300 ease-in-out md:hidden',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {/* Mobile header */}
        <div className="flex items-center justify-between h-14 px-4 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-2">
            {logo}
            <span className="font-semibold text-[var(--color-text-primary)]">{appName}</span>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] transition-colors"
            aria-label="閉じる"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        {renderSidebarContent(true)}
      </aside>

      {/* Desktop sidebar (collapsible to icon rail, resizable)
        外側 div は flex 行内の予約幅。展開時はサイドバー幅を確保して main と重ならず、
        折りたたみ時はレール幅だけを確保する。レール hover 時だけ main 上に重ねて展開する。 */}
      <div
        className={cn(
          'sticky top-14 z-[var(--z-sticky)] hidden h-[calc(100vh-3.5rem)] shrink-0 self-start overflow-visible ease-in-out md:block',
          !isResizing && 'transition-[width] duration-300',
        )}
        style={{ width: isCollapsed ? RAIL_WIDTH : expandedWidth }}
      >
        <aside
          onMouseEnter={() => {
            if (isCollapsed) setHovered(true);
          }}
          onMouseLeave={() => {
            if (isCollapsed) setHovered(false);
          }}
          className={cn(
            'absolute top-0 left-0 h-full flex flex-col bg-[var(--color-bg-surface)] border-r border-[var(--color-border)] ease-in-out',
            !isResizing && 'transition-[width] duration-300',
            isCollapsed && hovered && 'shadow-xl',
          )}
          style={{ width: showExpanded ? expandedWidth : RAIL_WIDTH }}
        >
          {/* ナビ本体。レール幅では overflow で内容をクリップする */}
          <div className="flex-1 w-full overflow-hidden">{renderSidebarContent(showExpanded)}</div>

          {/* 幅調整ハンドル（展開時のみ）。右端をドラッグして幅を変更する */}
          {!isCollapsed && (
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="サイドバーの幅を調整"
              onMouseDown={onResizeStart}
              className={cn(
                'absolute top-0 right-0 z-10 h-full w-1.5 cursor-col-resize transition-colors hover:bg-[var(--color-accent-primary)]/40',
                isResizing && 'bg-[var(--color-accent-primary)]/60',
              )}
            />
          )}

          {/* 展開/折りたたみトグル: サイドバー右端からはみ出すタブ（位置は従来どおり）。
            <aside> の子なのでタブへホバーしても onMouseLeave は発火しない。 */}
          {onToggleCollapse && (
            <button
              type="button"
              onClick={onToggleCollapse}
              className="absolute top-3 right-0 translate-x-full z-10 flex items-center justify-center w-5 h-8 rounded-r-md bg-[var(--color-bg-surface)] border border-l-0 border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] transition-colors"
              aria-label={isCollapsed ? 'サイドバーを開く' : 'サイドバーを折りたたむ'}
            >
              <svg
                className={cn(
                  'w-3 h-3 transition-transform duration-300 ease-in-out',
                  isCollapsed && 'rotate-180',
                )}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          )}
        </aside>
      </div>
    </>
  );
}
